import React from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, CSSProperties, ReactNode, ElementType } from 'react';

import clsx from 'clsx';

import styles from './Text.module.css';

export type TextColor =
    | 'primary'
    | 'emphasized'
    | 'secondary'
    | 'lightGray'
    | 'superLightGray'
    | 'error'
    | 'white'
    | 'white_base'
    | 'whiteRgba40'
    | 'whiteRgba64'
    | 'whiteRgba72'
    | 'black_base';

export type TextSize = 'display2' | 'display1' | 'header' | 'main' | 'secondary' | 'subheader' | 'tag' | 'note';

export type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'pre' | 'paragraph' | 'div' | 'span' | 'link' | 'button' | 'listItem';

export type TextIndent =
    | '4px'
    | '8px'
    | '12px'
    | '16px'
    | '24px'
    | '32px'
    | '48px'
    | '64px'
    | 'adaptive-8-16'
    | 'adaptive-16-24'
    | 'adaptive-24-32'
    | 'adaptive-32-48';

interface CommonTextStyleProps {
    color?: TextColor;
    size?: TextSize;
    smallCaps?: boolean;
    nowrap?: boolean;
    leftSpacer?: boolean;
    ellipsis?: boolean;
    indent?: TextIndent;
    lineThrough?: boolean;
    textAlign?: CSSProperties['textAlign'];
    flexShrink?: number | string;
}

type NativeTextElementProps = Omit<HTMLAttributes<HTMLElement> & AnchorHTMLAttributes<HTMLAnchorElement> & ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

export interface TextProps extends NativeTextElementProps, CommonTextStyleProps {
    variant?: TextVariant;
    children?: ReactNode;
    dataTestId?: string;
    className?: string;
}

const variantToTag: Record<TextVariant, ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    pre: 'pre',
    paragraph: 'p',
    div: 'div',
    span: 'span',
    link: 'a',
    button: 'button',
    listItem: 'li',
};

const sizeClass: Record<TextSize, string> = {
    display1: styles.sizeDisplay1,
    display2: styles.sizeDisplay2,
    header: styles.sizeHeader,
    main: styles.sizeMain,
    secondary: styles.sizeSecondary,
    subheader: styles.sizeSubheader,
    tag: styles.sizeTag,
    note: styles.sizeNote,
};

const colorClass: Record<TextColor, string> = {
    primary: styles.colorPrimary,
    emphasized: styles.colorEmphasized,
    secondary: styles.colorSecondary,
    lightGray: styles.colorLightGray,
    superLightGray: styles.colorSuperLightGray,
    error: styles.colorError,
    white: styles.colorWhite,
    white_base: styles.colorWhiteBase,
    whiteRgba40: styles.colorWhiteRgba40,
    whiteRgba64: styles.colorWhiteRgba64,
    whiteRgba72: styles.colorWhiteRgba72,
    black_base: styles.colorBlackBase,
};

const indentClass: Record<TextIndent, string> = {
    '4px': styles.indent4,
    '8px': styles.indent8,
    '12px': styles.indent12,
    '16px': styles.indent16,
    '24px': styles.indent24,
    '32px': styles.indent32,
    '48px': styles.indent48,
    '64px': styles.indent64,
    'adaptive-8-16': styles.indentAdaptive816,
    'adaptive-16-24': styles.indentAdaptive1624,
    'adaptive-24-32': styles.indentAdaptive2432,
    'adaptive-32-48': styles.indentAdaptive3248,
};

export const Text = ({
    variant = 'span',
    size,
    color = 'primary',
    indent,
    smallCaps,
    nowrap,
    leftSpacer,
    ellipsis,
    lineThrough,
    textAlign,
    flexShrink,
    className,
    style,
    dataTestId,
    children,
    ...rest
}: TextProps) => {
    const Tag = variantToTag[variant] ?? 'span';

    const classes = clsx(
        styles.text,
        size && sizeClass[size],
        colorClass[color],
        indent && indentClass[indent],
        variant === 'button' && styles.button,
        smallCaps && styles.smallCaps,
        nowrap && styles.nowrap,
        leftSpacer && styles.leftSpacer,
        ellipsis && styles.ellipsis,
        lineThrough && styles.lineThrough,
        className,
    );

    const hasInlineOverrides = textAlign !== undefined || flexShrink !== undefined;
    const mergedStyle: CSSProperties | undefined = hasInlineOverrides ? { textAlign, flexShrink, ...style } : style;

    return (
        <Tag {...rest} className={classes} style={mergedStyle} data-testid={dataTestId}>
            {children}
        </Tag>
    );
};
